import { useSession, useContent } from '../hooks/useSession';
import { gradeListing } from '../lib/curriculum';
import { Tick } from '../components/Icon';

/** Listing quality checker — a weighted self-audit scored out of 100. */
export function GraderPage() {
  const content = useContent();
  const { progress, toggle, clear } = useSession();

  const { score, tone, verdict, gaps } = gradeListing(content.gradeFields, progress.grade);

  const bg = tone === 'good' ? 'var(--success-bg)' : tone === 'mid' ? 'var(--warning-bg)' : 'var(--danger-bg)';
  const fg = tone === 'good' ? 'var(--green-500)' : tone === 'mid' ? 'var(--amber-500)' : 'var(--red-500)';

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tool</div>
      <h1 className="vu-h1">Listing quality checker</h1>
      <p className="vu-lede" style={{ maxWidth: '62ch' }}>
        Open one of your live listings next to this and tick what is genuinely true. The score
        is weighted by how much each field moves conversion on our platform.
      </p>

      <div
        className="vu-grid"
        style={{ gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)', marginTop: 'var(--space-6)', alignItems: 'start' }}
      >
        <div className="vu-card" style={{ padding: 'var(--space-4) var(--space-6)' }}>
          {content.gradeFields.map((field) => {
            const on = !!progress.grade[field.id];
            return (
              <button
                key={field.id}
                type="button"
                className="vu-check"
                onClick={() => toggle('grade', field.id, !on)}
                aria-pressed={on}
              >
                <span className="vu-box" data-on={on}>{on && <Tick />}</span>
                <span
                  style={{
                    color: on ? 'var(--text-muted)' : 'var(--text-body)',
                    textDecoration: on ? 'line-through' : 'none',
                  }}
                >
                  {field.label}
                </span>
                <span className="vu-check-pts">{field.weight} pts</span>
              </button>
            );
          })}
        </div>

        <div>
          <div
            className="vu-card"
            style={{ background: bg, borderColor: 'transparent', textAlign: 'center', padding: 'var(--space-8) var(--space-5)' }}
          >
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 56, fontWeight: 800, color: fg, lineHeight: 1 }}>
              {score}
            </div>
            <div
              style={{
                fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', letterSpacing: '.14em',
                color: fg, marginTop: 'var(--space-2)',
              }}
            >
              OUT OF 100
            </div>
            <p style={{ margin: 'var(--space-4) 0 0', fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)' }}>
              {verdict}
            </p>
          </div>

          <div className="vu-card" style={{ marginTop: 'var(--space-4)' }}>
            <h2 className="vu-h3" style={{ fontSize: 'var(--fs-lg)', marginBottom: 'var(--space-3)' }}>
              {gaps.length ? 'Fix these, hardest-hitting first' : 'Nothing missing'}
            </h2>
            {gaps.length ? (
              gaps.map((g) => (
                <div className="vu-gap" key={g.id}>
                  <span style={{ fontSize: 'var(--fs-sm)' }}>
                    {g.label} <span className="vu-subtle">(+{g.weight})</span>
                  </span>
                </div>
              ))
            ) : (
              <p className="vu-muted" style={{ margin: 0 }}>
                Every field is ticked. This listing is ready to publish.
              </p>
            )}
          </div>

          <button
            type="button"
            className="vu-btn vu-btn-ghost vu-noprint"
            style={{ marginTop: 'var(--space-4)', width: '100%' }}
            onClick={() => clear('grade')}
          >
            Start over for another listing
          </button>
        </div>
      </div>
    </div>
  );
}
